import React, { VFC, useState, createRef } from 'react';
import clsx from 'clsx';
import { unstable_batchedUpdates } from 'react-dom';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService, useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import {
  Box,
  Container,
  Button,
  makeStyles,
  Paper,
  Typography,
  AutocompleteOption,
  Tabs,
  Tab,
} from '@dxb-web-ui-framework/design-system';
import { FormBuilderPresenter } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/FormBuilderPresenter';
import { TFunction } from 'react-i18next';
import { ErrorAlert } from '../../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { TableGrid } from '../../../../content-composer/src/components/Generic/TableGrid';
import { GenericDrawer } from '../../../../content-composer/src/components/Generic/GenericDrawer';
import { UnknownBankDetails, ValueIdentifier } from '../types';
import { TransferServiceImpl } from '../../domain/Transfer/usecases';
import { SwiftBank, SwiftCity, SwiftCode, SwiftCountry } from '../../domain/Transfer/entities';
import { useBicSwiftsConfig } from './formConfigurations/useBicSwiftsConfig';
import { useBicManuallyConfig } from './formConfigurations/useBicManuallyConfig';

export interface AddBankDrawerDetailsProps {
  isOpen: boolean;
  t: TFunction;
  unknownBankDetails: Nullable<UnknownBankDetails>;
  onToggleDrawer: (data?: any) => void;
  service: TransferServiceImpl;
  bicQuery: Nullable<string>;
  setBicQuery: Function;
  setUnknownBankDetails: React.Dispatch<React.SetStateAction<UnknownBankDetails>>;
  setSelectedBankSwiftCode: Function;
}

const useStyles = makeStyles(theme => ({
  customDrawerBodyClass: {
    display: 'flex',
    height: '100%',
    overflow: 'auto',
    flexDirection: 'column',
  },
  tableGridContainer: {
    margin: 'auto',
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(12),
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      padding: 0,
    },
  },
  formContainer: {
    margin: 'auto',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      marginTop: theme.spacing(5),
      padding: `0px ${theme.spacing(3)}px`,
    },
  },
  formContainerError: {
    margin: 'auto',
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      marginBottom: theme.spacing(0),
      padding: `0px ${theme.spacing(3)}px`,
    },
  },
  errorContainer: {
    wordBreak: 'break-all',
  },
  labelValueText: {
    marginTop: theme.spacing(1),
    fontSize: theme.typography.body2.fontSize,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  formBuilderElementWrapperClass: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
}));

enum TabValues {
  'BicCode' = 0,
  'SwiftCodes' = 1,
}

const AddBankDetailsDrawer: VFC<AddBankDrawerDetailsProps> = ({
  isOpen,
  t,
  unknownBankDetails,
  onToggleDrawer,
  service,
  bicQuery,
  setBicQuery,
  setUnknownBankDetails,
  setSelectedBankSwiftCode,
}) => {
  const formRef = createRef<HTMLFormElement>();
  const [citiesTimestamp, setCitiesTimestamp] = useState(new Date().toISOString());
  const [banksTimestamp, setBanksTimestamp] = useState(new Date().toISOString());
  const [swiftCodesTimestamp, setSwiftCodesTimestamp] = useState(new Date().toISOString());
  const [shouldSubmitSwiftCodes, setShouldSubmitSwiftCodes] = useState(false);
  const [shouldSubmitBic, setShouldSubmitBic] = useState(false);
  const [activeTab, setActiveTab] = useState(TabValues.BicCode);
  const { isExtraSmallScreen: isMobile } = useScreenDetector();

  // available unknownBankDetails values and condition calls for the endpoints
  const countryName = unknownBankDetails?.bankCountry?.value;
  const cityName = unknownBankDetails?.bankCity?.value;
  const bankName = unknownBankDetails?.bankName?.value;
  const isBankCountry = !isEmpty(countryName);
  const isBankCity = !isEmpty(cityName);
  const isBankName = !isEmpty(bankName);

  // first we get the countries when the user clicks on swift tab
  const shouldFetchCountriesData = activeTab === TabValues.SwiftCodes;
  const {
    data: swiftCountriesData,
    error: swiftCountriesErrorData,
    isLoading: isLoadingSwiftCountries,
  } = useInvokeSWRService<SwiftCountry[]>(
    service.GetSwiftCountries,
    null,
    shouldFetchCountriesData,
    'transferService.GetSwiftCountries',
  );

  // chainable services according the data available in unknownBankDetails state
  const {
    data: swiftCitiesData,
    error: swiftCitiesErrorData,
    isLoading: isLoadingSwiftCities,
  } = useInvokeSWRService<SwiftCity[]>(
    service.GetSwiftCities,
    {
      countryName,
      meta: {
        timestamp: citiesTimestamp,
      },
    },
    isBankCountry,
    'transferService.GetSwiftCities',
  );

  const {
    data: swiftBanksData,
    error: swiftBanksErrorData,
    isLoading: isLoadingSwiftBanks,
  } = useInvokeSWRService<SwiftBank[]>(
    service.GetSwiftBanks,
    {
      cityName,
      countryName,
      meta: {
        timestamp: banksTimestamp,
      },
    },
    isBankCountry && isBankCity,
    'transferService.GetSwiftBanks',
  );

  const {
    data: swiftCodesData,
    error: swiftCodesErrorData,
    isLoading: isLoadingSwiftCodes,
  } = useInvokeSWRService<SwiftCode[]>(
    service.GetBankSwiftCodes,
    {
      cityName,
      countryName,
      bankName,
      meta: {
        timestamp: swiftCodesTimestamp,
      },
    },
    isBankCountry && isBankCity && isBankName && shouldSubmitSwiftCodes && activeTab === TabValues.SwiftCodes,
    'transferService.GetSwiftCodes',
  );

  const {
    data: bicSwiftCodeData,
    error: bicSwiftCodeErrorData,
    isLoading: isLoadingBicSwiftCode,
  } = useInvokeSWRService<SwiftCode>(
    service.GetSwiftCodesByBic,
    {
      bic: bicQuery,
      meta: {
        timestamp: swiftCodesTimestamp,
      },
    },
    Boolean(bicQuery) && shouldSubmitBic && activeTab === TabValues.BicCode,
    'transferService.GetSwiftCodesByBic',
  );

  // callbacks
  const onChangeHandler = (name: string, data: AutocompleteOption | null) => {
    if (shouldSubmitSwiftCodes) {
      setShouldSubmitSwiftCodes(false);
    }
    const date = new Date().toISOString();
    const methods = formRef?.current?.getFormActions();
    const formValues = formRef?.current?.getFormData();
    // handle the scenario when user is clearing the country all other fields should reset
    if (methods && formValues && name === ValueIdentifier.BANK_COUNTRY) {
      methods.setValue(ValueIdentifier.BANK_CITY, null);
      methods.setValue(ValueIdentifier.BANK_NAME, null);
      setUnknownBankDetails({
        [name]: data,
        [ValueIdentifier.BANK_CITY]: null,
        [ValueIdentifier.BANK_NAME]: null,
      });
      setCitiesTimestamp(date);
      return;
    }
    //handle the scenario when user is clearing the city the bank should be cleared and keep the already selected country
    if (methods && formValues && name === ValueIdentifier.BANK_CITY) {
      methods.setValue(ValueIdentifier.BANK_NAME, null);
      setUnknownBankDetails({
        [ValueIdentifier.BANK_COUNTRY]: formValues[ValueIdentifier.BANK_COUNTRY],
        [name]: data,
        [ValueIdentifier.BANK_NAME]: null,
      });
      setBanksTimestamp(date);
      return;
    }
    setUnknownBankDetails((prev: any) => ({ ...prev, [name]: data }));
  };

  const onSubmitSwiftCodes = async (formData: UnknownBankDetails, e: any) => {
    const { isValid, isDirty } = formRef.current?.getFormState();
    const { trigger } = formRef.current?.getFormActions();

    if (!isDirty && !unknownBankDetails) {
      setTimeout(() => trigger(), 1);
      return;
    }

    if (!isValid && isDirty) {
      trigger();
      return;
    }

    setUnknownBankDetails(formData);
    setSwiftCodesTimestamp(new Date().toISOString());
    setShouldSubmitSwiftCodes(true);
  };

  const onSubmitBic = async (formData: { bic: string }, e: any) => {
    const { isValid, isDirty } = formRef.current?.getFormState();
    const { trigger } = formRef.current?.getFormActions();

    if (!isDirty && !formData.bic) {
      setTimeout(() => trigger(), 1);
      return;
    }

    if (!isValid && isDirty) {
      trigger();
      return;
    }

    setSwiftCodesTimestamp(new Date().toISOString());
    setBicQuery(formData.bic);
    setShouldSubmitBic(true);
  };

  const onToggleDrawerWrapper = (data?: any) => {
    if (shouldSubmitSwiftCodes) {
      setShouldSubmitSwiftCodes(false);
    }
    if (shouldSubmitBic) {
      setShouldSubmitBic(false);
    }
    onToggleDrawer();
  };

  const onSelectBankSwiftCode = (data: SwiftCode) => {
    unstable_batchedUpdates(() => {
      setSelectedBankSwiftCode(data);
    });
    onToggleDrawerWrapper();
  };

  // errors
  const error =
    swiftCodesErrorData ||
    swiftCitiesErrorData ||
    swiftBanksErrorData ||
    bicSwiftCodeErrorData ||
    swiftCountriesErrorData;

  // remove defaultValues from data and create autocomplete values for each data
  const classes = useStyles();

  const formConfigBicSwifts = useBicSwiftsConfig(
    isBankCountry,
    isBankCity,
    isLoadingSwiftCodes,
    isLoadingSwiftCountries,
    isLoadingSwiftCities,
    isLoadingSwiftBanks,
    onChangeHandler,
    unknownBankDetails,
    swiftCountriesData,
    swiftCitiesData,
    swiftBanksData,
  );

  const formConfigBicManually = useBicManuallyConfig(isLoadingBicSwiftCode, bicQuery);

  return (
    <GenericDrawer
      disableMobileDrawerHeight={true}
      stickDrawerTitleToLeft={true}
      anchor='bottom'
      title={t('fundsTransferPage.Forms.GenericDrawer.SwiftCode')}
      isOpen={isOpen}
      customDrawerBodyClass={classes.customDrawerBodyClass}
      onToggleDrawer={onToggleDrawerWrapper}>
      {swiftCodesData || bicSwiftCodeData ? (
        <Container maxWidth='md' className={classes.tableGridContainer}>
          <Paper elevation={isMobile ? 0 : 1}>
            <Box display='flex' flexDirection='row' justifyContent='space-between' p={3} pb={1} pt={4}>
              <Typography color='primary'>{t('fundsTransferPage.Forms.GenericDrawer.Title.SelectYourBank')}</Typography>
            </Box>
            <TableGrid
              gridOptions={{
                ariaTitle: t('fundsTransferPage.Forms.GenericDrawer.TableGrid.AriaTitle'),
                dataTestId: 'Recent-transactions-table',
                noResultsMsg: t('fundsTransferPage.Forms.GenericDrawer.TableGrid.EmptyData'),
                padding: 'normal',
                size: 'medium',
                stickyHeader: false,
                collapseToList: true,
              }}
              columnDefs={[
                {
                  id: 'bankDescr',
                  field: 'bankDescr',
                  label: t('fundsTransferPage.Forms.GenericDrawer.TableGrid.BankName'),
                  cellProps: {
                    width: 'auto',
                  },
                  columnHeaderCellProps: {
                    width: 'auto',
                  },
                },
                {
                  id: 'location',
                  field: 'location',
                  label: t('fundsTransferPage.Forms.GenericDrawer.TableGrid.Location'),
                  cellProps: {
                    width: 'auto',
                  },
                  columnHeaderCellProps: {
                    width: 'auto',
                  },
                },
                {
                  id: 'Address',
                  field: 'address',
                  label: t('fundsTransferPage.Forms.GenericDrawer.TableGrid.Address'),
                  cellProps: {
                    width: 'auto',
                  },
                  columnHeaderCellProps: {
                    width: 'auto',
                  },
                },
                {
                  id: 'Swift Address',
                  field: 'bic',
                  label: t('fundsTransferPage.Forms.GenericDrawer.TableGrid.SwiftAddress'),
                  cellProps: {
                    width: 'auto',
                  },
                  columnHeaderCellProps: {
                    width: 'auto',
                  },
                },
                {
                  id: 'zipCodeTown',
                  field: 'zipCodeTown',
                  label: t('fundsTransferPage.Forms.GenericDrawer.TableGrid.zipCode'),
                  cellProps: {
                    width: 'auto',
                  },
                  columnHeaderCellProps: {
                    width: 'auto',
                  },
                },
                {
                  id: 'Select Icon',
                  field: 'actions',
                  label: '',
                  cellProps: { align: 'right' },
                  cellRenderer: rowData => (
                    <Button
                      size='small'
                      variant='text'
                      title={t('fundsTransferPage.Forms.GenericDrawer.TableGrid.Select.Button')}
                      onClick={() => onSelectBankSwiftCode(rowData as SwiftCode)}>
                      {t('fundsTransferPage.Forms.GenericDrawer.TableGrid.Select.Button')}
                    </Button>
                  ),
                },
              ]}
              rowData={activeTab === TabValues.BicCode && bicSwiftCodeData ? [bicSwiftCodeData] : swiftCodesData}
              mobileListConfig={{
                listItemContents: [
                  {
                    label: 'bankDescr',
                    value: 'address',
                    valueAreaLabel: 'bankDescr',
                    labelColor: 'textPrimary',
                    labelSize: 'large',
                    valueSize: 'small',
                    valueColor: 'secondary',
                    valueTransformer: (data: SwiftCode) => (
                      <Box component='span' display='flex' flexDirection='column'>
                        <Box mt={1} component='span' className={classes.labelValueText}>
                          {data.bic}
                        </Box>
                        {data.address && (
                          <Box mt={1} component='span' className={classes.labelValueText}>
                            {data.address}
                          </Box>
                        )}
                        {data.zipCodeTown && (
                          <Box mt={1} component='span' className={classes.labelValueText}>
                            {data.zipCodeTown}
                          </Box>
                        )}
                      </Box>
                    ),
                  },
                ],
                listItemOnSelect: rowData => onSelectBankSwiftCode(rowData as SwiftCode),
              }}
              hasSkeleton={isLoadingSwiftCodes}
            />
          </Paper>
        </Container>
      ) : (
        <Container
          maxWidth='sm'
          className={clsx({ [classes.formContainer]: isEmpty(error), [classes.formContainerError]: !isEmpty(error) })}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => {
              setActiveTab(value);
            }}
            aria-label={t('authorizationsListPage.Tabs.Authorizations')}>
            <Tab value={TabValues.BicCode} label={t('Insert BIC manually')}>
              {t('authorizationsListPage.Tab.Authorizations')}
            </Tab>
            <Tab value={TabValues.SwiftCodes} label={t('Insert BIC - Swift parameters ')}>
              {t('authorizationsListPage.Tab.Transactions')}
            </Tab>
          </Tabs>
          <FormBuilderPresenter
            submitAction={activeTab === TabValues.BicCode ? onSubmitBic : onSubmitSwiftCodes}
            ref={formRef}
            externalFormBuilderElementWrapperClass={classes.formBuilderElementWrapperClass}
            formConfig={activeTab === TabValues.BicCode ? formConfigBicManually : formConfigBicSwifts}>
            {error && !isEmpty(error) && (
              <Box mt={4} className={classes.errorContainer}>
                <ErrorAlert message={error.message} />
              </Box>
            )}
          </FormBuilderPresenter>
        </Container>
      )}
    </GenericDrawer>
  );
};

export { AddBankDetailsDrawer };
